import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import "./Blog.css";

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function BlogPost() {
  const { slug } = useParams();

  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // Comment form
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        const res = await fetch(
          `/.netlify/functions/get-post-by-slug?slug=${encodeURIComponent(slug)}`
        );
        const json = await res.json();
        if (!mounted) return;

        if (!res.ok || !json.ok) {
          setLoadError(json.error || "Post not found.");
          setPost(null);
          return;
        }
        setPost(json.post);
        setComments(json.comments || []);
        setLoadError("");
      } catch (err) {
        if (!mounted) return;
        setLoadError("Network error while loading the post.");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, [slug]);

  async function submitComment(e) {
    e.preventDefault();
    setSubmitError("");
    setSubmitting(true);
    try {
      const res = await fetch("/.netlify/functions/add-comment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ postId: post.id, name, body, website }),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) {
        setSubmitError(json.error || "Failed to submit comment.");
        setSubmitting(false);
        return;
      }
      setSubmitted(true);
      setName("");
      setBody("");
    } catch (err) {
      setSubmitError("Network error while submitting comment.");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <main className="page page--contained blog-post-page">
        <div className="page-status">Loading…</div>
      </main>
    );
  }

  if (loadError || !post) {
    return (
      <main className="page page--contained blog-post-page">
        <div className="page-status">{loadError || "Post not found."}</div>
        <p style={{ textAlign: "center" }}>
          <Link to="/blog" className="blog-back">← Back to blog</Link>
        </p>
      </main>
    );
  }

  const date = formatDate(post.publishedDate || post.createdAt);

  return (
    <main className="page page--contained blog-post-page">
      <article className="blog-article">
        <Link to="/blog" className="blog-back">← Back to blog</Link>

        <header className="blog-article-header">
          {post.category && (
            <span className="blog-card-cat">{post.category}</span>
          )}
          <h1 className="blog-article-title">{post.title}</h1>
          <p className="blog-meta">
            {post.author && <span>{post.author}</span>}
            {post.author && date && <span aria-hidden="true"> · </span>}
            {date && <span>{date}</span>}
          </p>
        </header>

        {post.coverImageUrl && (
          <div className="blog-article-cover">
            <img src={post.coverImageUrl} alt="" />
          </div>
        )}

        <div
          className="blog-article-body"
          dangerouslySetInnerHTML={{ __html: post.body || "" }}
        />

        {(post.tags || []).length > 0 && (
          <ul className="blog-article-tags" aria-label="Tags">
            {post.tags.map((t) => (
              <li key={t}>#{t}</li>
            ))}
          </ul>
        )}
      </article>

      <section className="blog-comments" aria-labelledby="comments-heading">
        <h2 id="comments-heading">
          Comments{comments.length > 0 ? ` (${comments.length})` : ""}
        </h2>

        {comments.length === 0 ? (
          <p className="blog-comments-empty">
            No comments yet. Be the first to share your thoughts.
          </p>
        ) : (
          <ul className="blog-comment-list">
            {comments.map((c) => (
              <li key={c.id} className="blog-comment">
                <div className="blog-comment-head">
                  <strong>{c.name}</strong>
                  {c.createdAt && (
                    <span className="blog-comment-date">
                      {formatDate(c.createdAt)}
                    </span>
                  )}
                </div>
                <p className="blog-comment-body">{c.body}</p>
              </li>
            ))}
          </ul>
        )}

        <div className="blog-comment-form-wrap">
          <h3>Leave a comment</h3>
          {submitted ? (
            <p className="blog-comment-thanks">
              Thank you. Your comment has been submitted and will appear after
              review.
            </p>
          ) : (
            <form className="blog-comment-form" onSubmit={submitComment}>
              <label className="blog-field">
                <span>Name</span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={80}
                  required
                />
              </label>

              <label className="blog-field">
                <span>Comment</span>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  maxLength={4000}
                  rows={4}
                  required
                />
              </label>

              {/* Honeypot: hidden from real users */}
              <input
                type="text"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  width: 1,
                  height: 1,
                  opacity: 0,
                }}
              />

              {submitError && <p className="blog-form-error">{submitError}</p>}

              <button
                type="submit"
                className="blog-submit"
                disabled={submitting}
              >
                {submitting ? "Submitting…" : "Submit comment"}
              </button>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
